<script setup lang="ts">
import { ref } from 'vue'
import CertificatesModal from './CertificatesModal.vue'
import Certificate from '~/ui-kit/login/Certificate.vue'

interface SeminarCertificate {
  license_number: string
  link: string
}

interface AceCertificate {
  name: string
  ace_certificate: string
  ace_link: string
  tutor_certificate?: string
  tutor_link?: string
}

interface CertEntry {
  seminar_id: number
  seminar_name: string
  certificates: SeminarCertificate[]
  aces: AceCertificate[]
  author_role?: string
  methodist?: boolean
}

interface Context {
  certificates?: CertEntry[]
  [key: string]: unknown
}

interface CertModalItem {
  icon: string
  title: string
  number: string
  href: string
  type?: string
}

defineProps<{
  context: Context
  isCurrentUser: boolean
}>()

const certificateModal = ref(false)
const certModalData = ref<CertModalItem[] | null>(null)

const swiperConfig = {
  slidesPerView: 'auto',
  spaceBetween: 16,
  freeMode: true,

  mousewheel: {
    releaseOnEdges: true
  },

  navigation: {
    nextEl: '.swiper-button-next',
    prevEl: '.swiper-button-prev'
  }
}

function openCertModal(cert: CertEntry) {
  const certs: CertModalItem[] = []

  for (const seminarCert of cert.certificates) {
    certs.push({
      icon: 'seminar-done',
      title: cert.seminar_name,
      number: seminarCert.license_number,
      href: seminarCert.link
    })
  }

  for (const aceCert of cert.aces) {
    certs.push({
      icon: 'cert-done',
      title: aceCert.name,
      type: 'Авторский сертификат',
      number: aceCert.ace_certificate,
      href: aceCert.ace_link
    })

    if (!aceCert.tutor_certificate || !aceCert.tutor_link) continue
    certs.push({
      icon: 'cert-tutor-done',
      title: aceCert.name,
      type: 'Сертификат тьютора',
      number: aceCert.tutor_certificate,
      href: aceCert.tutor_link
    })
  }

  certModalData.value = certs
}
</script>

<template>
  <div class="teacher-bio__tab teacher-bio__tab_achievements">
    <h2 class="text-body-bold">Семинары и аттестации</h2>

    <v-rich-modal v-model="certificateModal" title="Сертификат">
      <Certificate standalone />
    </v-rich-modal>

    <CertificatesModal v-model="certModalData" />

    <v-button
      v-if="!(context.certificates ?? []).length && isCurrentUser"
      class="mt-1"
      mode="primary flat"
      @click="
        () => {
          certificateModal = true
        }
      "
    >
      Ввести номер сертификата
    </v-button>

    <v-slider :config="swiperConfig" class="teacher-bio__cert-carousel mt-3">
      <div v-for="cert in context.certificates" :key="cert.seminar_id" class="swiper-slide teacher-bio__certificate">
        <div class="d-flex mb-1">
          <v-icon size="32" class="mr-half">seminar-done</v-icon>
          <v-icon v-for="(ace, i) in cert.aces" :key="i" size="32" class="mr-half">
            {{ ace.tutor_certificate ? 'cert-tutor-done' : 'cert-done' }}
          </v-icon>
        </div>

        <span class="text-body-bold flex-spacer" v-text="cert.seminar_name"></span>

        <!-- Not implemented on backend
        <v-link v-if="isCurrentUser">
          Моя группа
        </v-link> -->
        <v-link v-if="cert.author_role" mode="primary" href="" class="mt-half">
          <b>{{ cert.author_role }}</b>
        </v-link>
        <v-link v-else-if="cert.methodist" mode="primary" class="mt-half" @click="() => openCertModal(cert)">
          <b>Методист</b>
        </v-link>
        <v-link
          v-else-if="!cert.aces.length && cert.certificates.length === 1"
          mode="primary"
          :href="cert.certificates[0].link"
          target="_blank"
          class="mt-1"
        >
          <b>Сертификат</b>&nbsp;{{ cert.certificates[0].license_number }}
        </v-link>

        <v-link v-else mode="primary" class="d-flex align-center mt-1" @click="() => openCertModal(cert)">
          Сертификаты
          <v-icon size="16" class="ml-half">mdi-chevron-down</v-icon>
        </v-link>
      </div>
    </v-slider>
  </div>
</template>
